import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, XCircle, ShoppingBag, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomizeSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useAuth();
  
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const investmentId = searchParams.get('investment_id');
  const provider = searchParams.get('provider');
  const paypalToken = searchParams.get('token'); // PayPal passes token as param

  useEffect(() => {
    // PayPal flow: capture payment on return
    if (provider === 'paypal' && paypalToken) {
      handlePayPalCapture();
      return;
    }
    
    // Stripe flow: poll for session status
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    
    const pollStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus('failed');
        return;
      }
      
      try {
        const res = await axios.get(`${API}/customize/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          return;
        }
        
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, 2000);
      } catch (err) {
        console.error('Status check error:', err);
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, 2000);
      }
    };
    
    pollStatus();
  }, [sessionId, token, attempts, provider]);

  const handlePayPalCapture = async () => {
    try {
      const captureEndpoint = investmentId 
        ? `${API}/paypal/capture-investment` 
        : `${API}/paypal/capture-order`;
      const captureData = {
        paypal_order_id: paypalToken,
        order_id: investmentId || orderId,
      };
      
      const res = await axios.post(captureEndpoint, captureData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.status === 'success') {
        setStatus('success');
      } else {
        setStatus('failed');
      }
    } catch (err) {
      console.error('PayPal capture error:', err);
      setStatus('failed');
    }
  };

  return (
    <div className="min-h-screen bg-amber-50 flex items-center justify-center pt-16 px-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-orange-500 animate-spin mb-4" />
              <h1 className="text-2xl font-bold mb-2">{t('customize.checkingPayment', '正在确认支付...')}</h1>
              <p className="text-amber-600">{t('customize.pleaseWait', '请稍候，正在验证您的支付状态')}</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <h1 className="text-2xl font-bold mb-2">{t('customize.paymentSuccess', '支付成功！')}</h1>
              <p className="text-amber-600 mb-6">
                {orderId 
                  ? t('customize.orderSuccess', '您的定制订单已创建，我们将尽快为您生产')
                  : t('customize.investmentSuccess', '您的投资已确认，将在产品销售后获得收益分成')
                }
              </p>
              <div className="flex flex-col gap-3">
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  onClick={() => navigate('/profile/me?tab=customize')}
                >
                  {orderId ? <ShoppingBag className="w-4 h-4 mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
                  {t('customize.viewOrders', '查看我的订单')}
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/artworks')}
                >
                  {t('customize.continueShopping', '继续浏览作品')}
                </Button>
              </div>
            </>
          )}
          
          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
              <h1 className="text-2xl font-bold mb-2">{t('customize.paymentFailed', '支付确认失败')}</h1>
              <p className="text-amber-600 mb-6">{t('customize.paymentFailedDesc', '无法确认支付状态，请联系客服或稍后重试')}</p>
              <Button 
                className="w-full"
                onClick={() => navigate('/artworks')}
              >
                {t('common.back', '返回')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomizeSuccessPage;
